import React, { useEffect, useState, useCallback } from "react";
import {
  Drawer, Spin, Alert, Tag, Input, Button, Select, DatePicker,
  Timeline as AntTimeline, message, Popconfirm, Space, Divider, Steps, Tooltip,
} from "antd";
import {
  PhoneOutlined, MailOutlined, EnvironmentOutlined, TagOutlined,
  ClockCircleOutlined, UserOutlined, EditOutlined, DeleteOutlined,
  SendOutlined, SyncOutlined, StopOutlined, PlayCircleOutlined,
  LinkOutlined, WhatsAppOutlined, CheckCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import customClientAxios from "../../customAxios";
import {
  AdminLeads,
  AdminLeadResyncHubspot,
  AdminLeadOptOutNurture,
  AdminLeadResumeNurture,
  AdminNurtureMessages,
} from "../../utils/ApiEndpoints";
import styled from "styled-components";

const { TextArea } = Input;

// ─── Styled helpers ────────────────────────────────────────────────────────
const Section = styled.div`margin-bottom: 18px;`;
const SectionHeading = styled.div`
  font-size: 11px; font-weight: 700; color: #64748b;
  text-transform: uppercase; letter-spacing: 0.06em;
  margin-bottom: 8px; padding-bottom: 6px;
  border-bottom: 1px solid #f1f5f9;
  display: flex; align-items: center; justify-content: space-between;
`;
const InfoRow = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 5px 0; font-size: 13px; color: #374151;
  svg { color: #94a3b8; font-size: 14px; }
`;
const Card = styled.div`
  background: ${(p) => p.$bg || "#f8fafc"};
  border: 1px solid ${(p) => p.$border || "#e2e8f0"};
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 10px;
  .row { display: flex; justify-content: space-between; align-items: center; }
  .label { font-size: 11px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
  .value { font-size: 13px; color: #1e293b; font-weight: 500; }
`;
const FunnelBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  background: linear-gradient(135deg, ${(p) => p.$from || "#6366f1"}, ${(p) => p.$to || "#8b5cf6"});
  color: white;
  font-size: 12px;
  font-weight: 600;
`;
const SocialChip = styled.a`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 10px; border-radius: 12px; font-size: 11px;
  background: #f1f5f9; color: #6366f1; text-decoration: none;
  margin-right: 6px; margin-bottom: 4px;
  &:hover { background: #e2e8f0; }
`;
const NoteInput = styled.div`display: flex; gap: 8px; margin-top: 8px;`;

// ─── Constants ─────────────────────────────────────────────────────────────
const STAGE_COLORS = {
  new: "blue",
  contacted: "cyan",
  qualified: "purple",
  negotiating: "orange",
  won: "green",
  activated: "lime",
  purchased: "gold",
  lost: "red",
};
const STAGE_OPTIONS = [
  "new",
  "contacted",
  "qualified",
  "negotiating",
  "won",
  "activated",
  "purchased",
  "lost",
];
const PRIORITY_OPTIONS = ["hot", "warm", "cold"];
const ACTIVITY_TYPES = [
  { value: "note", label: "Note" },
  { value: "called", label: "Called" },
  { value: "emailed", label: "Emailed" },
  { value: "messaged", label: "Messaged" },
  { value: "met", label: "Met" },
];

const TIMELINE_COLORS = {
  lead_created: "green",
  stage_changed: "blue",
  priority_changed: "orange",
  note_added: "gray",
  activity_logged: "purple",
  follow_up_set: "cyan",
  user_linked: "green",
  tag_changed: "gray",
  field_updated: "gray",
};
const ACTIVITY_ICONS = { called: "📞", emailed: "📧", messaged: "💬", met: "🤝" };

// Funnel mapping — mirrors backend services/hubspot/mappers.js
const FUNNEL_STAGE = {
  new:         { label: "Prospect",  index: 0, from: "#6366f1", to: "#8b5cf6" },
  contacted:   { label: "Prospect",  index: 0, from: "#6366f1", to: "#8b5cf6" },
  qualified:   { label: "Lead",      index: 1, from: "#8b5cf6", to: "#a855f7" },
  negotiating: { label: "Lead",      index: 1, from: "#8b5cf6", to: "#a855f7" },
  won:         { label: "Onboarded", index: 2, from: "#10b981", to: "#059669" },
  activated:   { label: "Activated", index: 3, from: "#84cc16", to: "#65a30d" },
  purchased:   { label: "Purchased", index: 4, from: "#f59e0b", to: "#d97706" },
  lost:        { label: "Lost",      index: -1, from: "#94a3b8", to: "#64748b" },
};

// HubSpot account ID for direct linking. Replace if your account differs.
const HUBSPOT_ACCOUNT_ID = process.env.REACT_APP_HUBSPOT_ACCOUNT_ID || "246130222";
const hubspotContactUrl = (id) =>
  `https://app.hubspot.com/contacts/${HUBSPOT_ACCOUNT_ID}/contact/${id}`;

// ─── Component ─────────────────────────────────────────────────────────────
function LeadDetailDrawer({ leadId, open, onClose, onUpdate }) {
  const [lead, setLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [activityType, setActivityType] = useState("note");
  const [submitting, setSubmitting] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editFields, setEditFields] = useState({});

  const fetchLead = useCallback(async () => {
    if (!open || !leadId) return;
    setLoading(true);
    setError(null);
    try {
      const [leadRes, msgsRes] = await Promise.all([
        customClientAxios().get(`${AdminLeads}/${leadId}`),
        customClientAxios().get(AdminNurtureMessages, { params: { leadId, limit: 30 } }),
      ]);
      setLead(leadRes.data.data);
      setMessages(msgsRes.data?.data?.messages || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to load lead");
    } finally {
      setLoading(false);
    }
  }, [leadId, open]);

  useEffect(() => { fetchLead(); }, [fetchLead]);

  const handleQuickUpdate = async (field, value) => {
    try {
      await customClientAxios().patch(`${AdminLeads}/${leadId}`, { [field]: value });
      fetchLead();
      onUpdate?.();
    } catch (e) {
      message.error("Failed to update");
    }
  };

  const handleAddEntry = async () => {
    if (!noteText.trim()) return;
    setSubmitting(true);
    try {
      if (activityType === "note") {
        await customClientAxios().post(`${AdminLeads}/${leadId}/note`, { content: noteText });
      } else {
        await customClientAxios().post(`${AdminLeads}/${leadId}/activity`, {
          activityType, content: noteText,
        });
      }
      setNoteText("");
      fetchLead();
    } catch {
      message.error("Failed to add entry");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await customClientAxios().patch(`${AdminLeads}/${leadId}`, editFields);
      setEditing(false);
      setEditFields({});
      fetchLead();
      onUpdate?.();
      message.success("Lead updated");
    } catch {
      message.error("Failed to save");
    }
  };

  const handleDelete = async () => {
    try {
      await customClientAxios().delete(`${AdminLeads}/${leadId}`);
      message.success("Lead deleted");
      onClose();
      onUpdate?.();
    } catch {
      message.error("Failed to delete");
    }
  };

  // ── Funnel actions ─────────────────────────────────────────────────────────
  const runAction = async (key, fn) => {
    setBusyAction(key);
    try {
      await fn();
      await fetchLead();
      onUpdate?.();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e?.response?.data?.message || e.message || "Action failed");
    } finally {
      setBusyAction(null);
    }
  };

  const resyncHubspot = () =>
    runAction("resync-hubspot", async () => {
      const { data } = await customClientAxios().post(AdminLeadResyncHubspot(leadId));
      message.success(`Resynced — HubSpot contact ${data?.data?.hubspotContactId}`);
    });

  const optOutNurture = () =>
    runAction("opt-out", async () => {
      const { data } = await customClientAxios().post(AdminLeadOptOutNurture(leadId));
      message.success(`Opted out — ${data?.data?.cancelledJobs || 0} pending messages cancelled`);
    });

  const resumeNurture = () =>
    runAction("resume", async () => {
      const { data } = await customClientAxios().post(AdminLeadResumeNurture(leadId));
      message.success(`Nurture resumed — ${data?.data?.scheduled || 0} steps scheduled`);
    });

  // ─── Render ─────────────────────────────────────────────────────────────
  const funnel = lead ? FUNNEL_STAGE[lead.stage] : null;

  return (
    <Drawer
      title={
        lead ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontWeight: 700 }}>{lead.name || "Unnamed lead"}</span>
            {funnel && (
              <FunnelBadge $from={funnel.from} $to={funnel.to}>
                {funnel.label}
              </FunnelBadge>
            )}
            <Tag color={STAGE_COLORS[lead.stage]} style={{ fontSize: 11 }}>{lead.stage}</Tag>
          </div>
        ) : "Lead Detail"
      }
      placement="right"
      width={620}
      open={open}
      onClose={() => { setEditing(false); onClose(); }}
      destroyOnClose
      extra={
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => {
            setEditing(!editing);
            if (!editing) setEditFields({
              name: lead?.name, email: lead?.email, phone: lead?.phone,
              category: lead?.category, city: lead?.city,
            });
          }}>{editing ? "Cancel" : "Edit"}</Button>
          <Popconfirm title="Delete this lead?" onConfirm={handleDelete} okText="Delete" okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      }
    >
      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
      <Spin spinning={loading}>
        {lead && (
          <>
            {/* ── Funnel stepper ─────────────────────────────────────────── */}
            <Section>
              <SectionHeading>Funnel</SectionHeading>
              <Steps
                size="small"
                current={funnel?.index >= 0 ? funnel.index : 0}
                status={lead.stage === "lost" ? "error" : "process"}
                items={[
                  { title: "Prospect", description: "In WA nurture" },
                  { title: "Lead", description: "Sales-qualified" },
                  { title: "Onboarded", description: "Signed up" },
                  { title: "Activated", description: "Engaged" },
                  { title: "Purchased", description: "Paying plan" },
                ]}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                <Select
                  value={lead.stage}
                  onChange={(v) => handleQuickUpdate("stage", v)}
                  style={{ width: 140 }}
                  size="small"
                  options={STAGE_OPTIONS.map((s) => ({ value: s, label: s }))}
                />
                <Select
                  value={lead.priority}
                  onChange={(v) => handleQuickUpdate("priority", v)}
                  style={{ width: 100 }}
                  size="small"
                  options={PRIORITY_OPTIONS.map((p) => ({ value: p, label: p }))}
                />
                <DatePicker
                  size="small"
                  placeholder="Follow-up"
                  value={lead.followUpDate ? dayjs(lead.followUpDate) : null}
                  onChange={(d) => handleQuickUpdate("followUpDate", d ? d.toISOString() : null)}
                  style={{ width: 140 }}
                />
              </div>
            </Section>

            {/* ── HubSpot card ──────────────────────────────────────────── */}
            <Section>
              <SectionHeading>HubSpot Sync</SectionHeading>
              <Card $bg="#fff7ed" $border="#fed7aa">
                <div className="row" style={{ marginBottom: 6 }}>
                  <div className="label">Status</div>
                  <div>
                    {lead.hubspotContactId ? (
                      <Tag color="green" icon={<CheckCircleOutlined />}>Synced</Tag>
                    ) : lead.hubspotSyncError ? (
                      <Tooltip title={lead.hubspotSyncError}>
                        <Tag color="red">Error</Tag>
                      </Tooltip>
                    ) : (
                      <Tag>Pending</Tag>
                    )}
                  </div>
                </div>
                {lead.hubspotContactId && (
                  <div className="row" style={{ marginBottom: 6 }}>
                    <div className="label">Contact ID</div>
                    <a
                      href={hubspotContactUrl(lead.hubspotContactId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 12, fontFamily: "monospace", color: "#f4845a" }}
                    >
                      {lead.hubspotContactId} <LinkOutlined style={{ fontSize: 11 }} />
                    </a>
                  </div>
                )}
                {lead.hubspotSyncedAt && (
                  <div className="row" style={{ marginBottom: 6 }}>
                    <div className="label">Last synced</div>
                    <div className="value" style={{ fontSize: 11, color: "#64748b" }}>
                      {dayjs(lead.hubspotSyncedAt).format("MMM D, YYYY HH:mm")}
                    </div>
                  </div>
                )}
                <Button
                  size="small"
                  icon={<SyncOutlined />}
                  loading={busyAction === "resync-hubspot"}
                  onClick={resyncHubspot}
                  block
                  style={{ marginTop: 4 }}
                >
                  Resync to HubSpot
                </Button>
              </Card>
            </Section>

            {/* ── WhatsApp Nurture card ─────────────────────────────────── */}
            <Section>
              <SectionHeading>WhatsApp Nurture</SectionHeading>
              <Card $bg="#eff6ff" $border="#bfdbfe">
                <div className="row" style={{ marginBottom: 6 }}>
                  <div className="label">Status</div>
                  <div>
                    {lead.optedOutOfNurture ? (
                      <Tag color="default">Opted out{lead.optedOutAt ? ` ${dayjs(lead.optedOutAt).format("MMM D")}` : ""}</Tag>
                    ) : lead.nurtureStatus === "active" ? (
                      <Tag color="blue">Active</Tag>
                    ) : lead.nurtureStatus === "completed" ? (
                      <Tag color="green">Completed</Tag>
                    ) : (
                      <Tag>{lead.nurtureStatus || "not_started"}</Tag>
                    )}
                  </div>
                </div>
                <div className="row" style={{ marginBottom: 6 }}>
                  <div className="label">Phone (E.164)</div>
                  <div className="value" style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {lead.phoneE164 || <span style={{ color: "#ef4444" }}>missing</span>}
                  </div>
                </div>
                <div className="row" style={{ marginBottom: 6 }}>
                  <div className="label">Messages</div>
                  <div className="value" style={{ fontSize: 12 }}>
                    {messages.length} {messages.length === 1 ? "send" : "sends"}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  {lead.optedOutOfNurture ? (
                    <Button
                      size="small"
                      icon={<PlayCircleOutlined />}
                      loading={busyAction === "resume"}
                      onClick={resumeNurture}
                      block
                    >
                      Resume nurture
                    </Button>
                  ) : (
                    <Popconfirm
                      title="Opt out of nurture?"
                      description="Cancels all pending WA messages and marks the lead as opted-out."
                      onConfirm={optOutNurture}
                      okText="Opt out"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        size="small"
                        icon={<StopOutlined />}
                        danger
                        loading={busyAction === "opt-out"}
                        block
                      >
                        Opt out of nurture
                      </Button>
                    </Popconfirm>
                  )}
                </div>
              </Card>

              {/* Per-step nurture messages timeline */}
              {messages.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {messages
                    .slice()
                    .sort((a, b) => (a.stepDay || 99) - (b.stepDay || 99))
                    .map((m) => (
                      <div
                        key={m.messageId}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          padding: "6px 10px", marginBottom: 4, background: "#fff",
                          border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Tag color="purple" style={{ margin: 0 }}>D{m.stepDay}</Tag>
                          <WhatsAppOutlined style={{ color: "#10b981" }} />
                          <span style={{ color: "#64748b", fontFamily: "monospace", fontSize: 11 }}>
                            {m.templateKey}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {m.clickedAt && (
                            <Tooltip title={`Clicked ${dayjs(m.clickedAt).format("MMM D HH:mm")}`}>
                              <Tag color="success" style={{ margin: 0, fontSize: 10 }}>clicked</Tag>
                            </Tooltip>
                          )}
                          {m.readAt && !m.clickedAt && (
                            <Tooltip title={`Read ${dayjs(m.readAt).format("MMM D HH:mm")}`}>
                              <Tag color="purple" style={{ margin: 0, fontSize: 10 }}>read</Tag>
                            </Tooltip>
                          )}
                          {!m.readAt && m.deliveredAt && (
                            <Tooltip title={`Delivered ${dayjs(m.deliveredAt).format("MMM D HH:mm")}`}>
                              <Tag color="geekblue" style={{ margin: 0, fontSize: 10 }}>delivered</Tag>
                            </Tooltip>
                          )}
                          {!m.deliveredAt && m.sentAt && (
                            <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>sent</Tag>
                          )}
                          {m.status === "skipped" && (
                            <Tooltip title={m.skippedReason || "skipped"}>
                              <Tag color="warning" style={{ margin: 0, fontSize: 10 }}>skipped</Tag>
                            </Tooltip>
                          )}
                          {m.status === "failed" && (
                            <Tooltip title={m.failureReason || "failed"}>
                              <Tag color="error" style={{ margin: 0, fontSize: 10 }}>failed</Tag>
                            </Tooltip>
                          )}
                          {m.status === "queued" && !m.sentAt && (
                            <Tag style={{ margin: 0, fontSize: 10 }}>queued</Tag>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </Section>

            {/* ── Contact info ──────────────────────────────────────────── */}
            <Section>
              <SectionHeading>Contact</SectionHeading>
              {editing ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <Input size="small" placeholder="Name" value={editFields.name}
                    onChange={(e) => setEditFields({ ...editFields, name: e.target.value })} prefix={<UserOutlined />} />
                  <Input size="small" placeholder="Email" value={editFields.email}
                    onChange={(e) => setEditFields({ ...editFields, email: e.target.value })} prefix={<MailOutlined />} />
                  <Input size="small" placeholder="Phone" value={editFields.phone}
                    onChange={(e) => setEditFields({ ...editFields, phone: e.target.value })} prefix={<PhoneOutlined />} />
                  <Input size="small" placeholder="Category" value={editFields.category}
                    onChange={(e) => setEditFields({ ...editFields, category: e.target.value })} />
                  <Input size="small" placeholder="City" value={editFields.city}
                    onChange={(e) => setEditFields({ ...editFields, city: e.target.value })} prefix={<EnvironmentOutlined />} />
                  <Button size="small" type="primary" onClick={handleSaveEdit}>Save</Button>
                </div>
              ) : (
                <>
                  {lead.phone && <InfoRow><PhoneOutlined />{lead.phone}</InfoRow>}
                  {lead.email && <InfoRow><MailOutlined />{lead.email}</InfoRow>}
                  {lead.city && <InfoRow><EnvironmentOutlined />{lead.city}</InfoRow>}
                  {lead.category && <InfoRow><TagOutlined />{lead.category}</InfoRow>}
                </>
              )}
            </Section>

            {lead.socialLinks?.length > 0 && (
              <Section>
                <SectionHeading>Socials</SectionHeading>
                <div>
                  {lead.socialLinks.map((s, i) => (
                    <SocialChip key={i} href={s.url} target="_blank" rel="noopener noreferrer">
                      {s.platform}
                    </SocialChip>
                  ))}
                </div>
              </Section>
            )}

            <Section>
              <SectionHeading>Tags</SectionHeading>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {(lead.tags || []).map((t) => <Tag key={t} style={{ fontSize: 11 }}>{t}</Tag>)}
                {(!lead.tags || lead.tags.length === 0) && (
                  <span style={{ color: "#94a3b8", fontSize: 12 }}>No tags</span>
                )}
              </div>
            </Section>

            <Section>
              <SectionHeading>Details</SectionHeading>
              <InfoRow><ClockCircleOutlined />Created: {new Date(lead.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</InfoRow>
              <InfoRow>Source: <Tag style={{ fontSize: 11 }}>{lead.source}</Tag></InfoRow>
              {lead.sourceUrl && (
                <InfoRow>URL: <a href={lead.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "#6366f1" }}>{lead.sourceUrl}</a></InfoRow>
              )}
              {lead.wonLostReason && <InfoRow>Reason: {lead.wonLostReason}</InfoRow>}
              {lead.linkedUserId && (
                <InfoRow>Linked customer: <Tag color="green" style={{ fontSize: 11 }}>{lead.linkedUserId}</Tag></InfoRow>
              )}
              {lead.linkedBrandPageId && (
                <InfoRow>Brand page: <Tag color="purple" style={{ fontSize: 11 }}>{lead.linkedBrandPageId}</Tag></InfoRow>
              )}
            </Section>

            <Divider />

            {/* ── Add note / activity ───────────────────────────────────── */}
            <Section>
              <SectionHeading>Add Entry</SectionHeading>
              <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
                <Select
                  value={activityType}
                  onChange={setActivityType}
                  options={ACTIVITY_TYPES}
                  size="small"
                  style={{ width: 120 }}
                />
              </div>
              <NoteInput>
                <TextArea
                  rows={2}
                  placeholder={activityType === "note" ? "Add a note..." : `Log ${activityType} activity...`}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  onPressEnter={(e) => { if (e.ctrlKey) handleAddEntry(); }}
                />
                <Button type="primary" icon={<SendOutlined />} loading={submitting}
                  onClick={handleAddEntry} disabled={!noteText.trim()} />
              </NoteInput>
            </Section>

            {/* ── Timeline ──────────────────────────────────────────────── */}
            <Section>
              <SectionHeading>Timeline ({(lead.timeline || []).length})</SectionHeading>
              <AntTimeline
                style={{ marginTop: 12 }}
                items={(lead.timeline || []).map((entry) => ({
                  color: TIMELINE_COLORS[entry.type] || "gray",
                  children: (
                    <div>
                      <div style={{ fontSize: 12, color: "#1e293b" }}>
                        {entry.metadata?.activityType ? `${ACTIVITY_ICONS[entry.metadata.activityType] || ""} ` : ""}
                        {entry.content}
                      </div>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                        {new Date(entry.createdAt).toLocaleString("en-IN", {
                          day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                        })}
                      </div>
                    </div>
                  ),
                }))}
              />
            </Section>
          </>
        )}
      </Spin>
    </Drawer>
  );
}

export default LeadDetailDrawer;
